import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type { Thing } from 'schema-dts';
import { PostCategory } from '@/entities';
import { ApiNewsRequest, getCommonPageProps, getNews, getPostCategories } from '@/shared/api';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import JsonLd from '@/shared/components/shared/JsonLd';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateNewsJsonLd } from '@/shared/lib/utils/news-json-ld';
import NewsPageView, { NewsPageViewProps } from '@/views/NewsPageView';
import { pageLimits } from '@/views/NewsPageView/NewsPageView';

const NewsPage = ({
    meta,
    h1,
    categorySlug,
    mappedCategories,
    subscribeText,
    breadcrumbs,
    description,
    initialNewsResponse,
    categories,
    filter,
    initialParams = {},
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    return (
        <>
            <NewsPageView
                key={categorySlug}
                meta={meta}
                h1={h1}
                categorySlug={categorySlug}
                subscribeText={subscribeText}
                description={description}
                initialNewsResponse={initialNewsResponse}
                initialParams={initialParams}
                categories={categories}
                filter={filter}
                breadcrumbs={breadcrumbs}
                mappedCategories={mappedCategories}
            />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-news"
                data={generateNewsJsonLd({
                    h1,
                    pageDescription: description,
                    listingPath: '/news/',
                    articles: initialNewsResponse.data?.items || [],
                    breadcrumbs,
                })}
            />
        </>
    );
};

export default NewsPage;

type NewsPageProps = NonUndefined<CommonPageProps & NewsPageViewProps>;

export const getServerSideProps: GetServerSideProps<NewsPageProps> = async ({ query }) => {
    const initialParams: ApiNewsRequest = {
        limit: pageLimits[0],
    };

    if (typeof query.limit === 'string') {
        initialParams.limit = parseInt(query.limit);

        if (!pageLimits.includes(initialParams.limit)) {
            initialParams.limit = pageLimits[0];
        }
    }

    if (typeof query.page === 'string') {
        initialParams.page = parseInt(query.page);
    }

    if (typeof query.q === 'string') {
        initialParams.q = query.q;
    }

    const postCategoriesResponse = await getPostCategories();
    const categories = postCategoriesResponse.data || [];

    const [commonPageProps, pageResponse, newsResponse, siteSettings] = await Promise.all([
        getCommonPageProps(),
        getPageDetail({ params: { slug: 'posts' } }),
        getNews({ params: initialParams, postCategories: categories }),
        getSiteSettings({
            params: {
                keys: ['post_first_screen_title', 'post_first_screen_desription', 'post_first_screen_button_text'],
            },
        }),
    ]);

    const addedAllCategory =
        categories.length > 1 ? [{ name: 'Все', slug: '', id: '' } satisfies PostCategory, ...categories] : [];
    const mappedCategories = addedAllCategory.map((item) => ({ name: item.name }));

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'news-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Новости', href: '/news' },
            ],
            categorySlug: null,
            categories,
            mappedCategories,
            filter: addedAllCategory,
            initialNewsResponse: newsResponse,
            initialParams,
            h1: siteSettings.data?.post_first_screen_title || 'Новости форума и партнеров',
            subscribeText: siteSettings.data?.post_first_screen_button_text || 'Подписаться',
            description:
                siteSettings.data?.post_first_screen_desription ||
                'Подпишитесь на рассылку, чтобы быть в курсе актуальных новостей',
        },
    };
};
